import React, { useState, useEffect } from "react";
import axios from "axios";
import { buildApiUrl, API_CONFIG } from "../config/api";
import FormSelection from "./FormSelection";

interface BrandingInfo {
  branding: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    logo: string;
    logoUrl: string;
    theme: string;
    fontFamily: string;
    customCSS: string;
    footerText: string;
    contactInfo: {
      phone: string;
      email: string;
      address: string;
    };
  };
  patientInfo: {
    patient_id: string;
    patient_date_of_birth: string;
    location_id: string;
  };
}

interface DOBVerificationProps {
  token: string;
}

const DOBVerification: React.FC<DOBVerificationProps> = ({ token }) => {
  const [brandingInfo, setBrandingInfo] = useState<BrandingInfo | null>(null);
  const [dob, setDob] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [dobError, setDobError] = useState("");
  const [jwtToken, setJwtToken] = useState<string | null>(null);

  useEffect(() => {
    fetchBrandingInfo();
  }, [token]);

  const fetchBrandingInfo = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        buildApiUrl(API_CONFIG.ENDPOINTS.BRANDING, token)
      );
      setBrandingInfo(response.data);
    } catch (error) {
      console.error("Error fetching branding info:", error);
      setError("Failed to load patient information. Please check your link.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDOBVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dob) {
      setDobError("Please enter your date of birth");
      return;
    }

    try {
      setIsLoading(true);
      setDobError("");
      setError("");

      const response = await axios.post(
        buildApiUrl(API_CONFIG.ENDPOINTS.VERIFY_DOB, token),
        {
          dob: dob,
        }
      );

      if (response.data.isValid && response.data.jwtToken) {
        setIsVerified(true);
        setJwtToken(response.data.jwtToken);
      } else {
        setDobError(
          "Date of birth does not match our records. Please try again."
        );
      }
    } catch (error) {
      console.error("Error verifying DOB:", error);
      setDobError("Failed to verify date of birth. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerified && jwtToken) {
    return (
      <FormSelection
        token={token}
        jwtToken={jwtToken}
        brandingInfo={brandingInfo}
      />
    );
  }

  if (isLoading && !brandingInfo) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  if (error && !brandingInfo) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "Arial, sans-serif",
          color: "#d32f2f",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const primaryColor = brandingInfo?.branding?.primaryColor || "#2c5aa0";
  const secondaryColor = brandingInfo?.branding?.secondaryColor || "#f8f9fa";
  const fontFamily = brandingInfo?.branding?.fontFamily || "Inter, sans-serif";

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: secondaryColor,
        fontFamily: fontFamily,
      }}
    >
      {/* Header */}
      <header
        style={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
          color: "white",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        {brandingInfo?.branding?.logoUrl && (
          <img
            src={brandingInfo.branding.logoUrl}
            alt="Logo"
            style={{
              height: "60px",
              marginBottom: "1rem",
              borderRadius: "8px",
            }}
          />
        )}
        <h1 style={{ margin: 0, fontSize: "2rem" }}>Patient Intake Form</h1>
        <p style={{ margin: "0.5rem 0 0 0", opacity: 0.9 }}>
          Please verify your identity to continue
        </p>
      </header>

      {/* Main Content */}
      <main
        style={{
          maxWidth: "600px",
          margin: "3rem auto",
          padding: "0 2rem",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "2rem",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h2
            style={{
              color: primaryColor,
              marginBottom: "1rem",
              textAlign: "center",
            }}
          >
            Identity Verification
          </h2>

          <p
            style={{
              textAlign: "center",
              color: "#666",
              marginBottom: "2rem",
              lineHeight: "1.5",
            }}
          >
            To access your intake form, please enter your date of birth to
            verify your identity.
          </p>

          <form onSubmit={handleDOBVerification}>
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Date of Birth *
              </label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  fontFamily: fontFamily,
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = primaryColor)}
                onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                required
              />
            </div>

            {dobError && (
              <div
                style={{
                  backgroundColor: "#ffebee",
                  color: "#d32f2f",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  marginBottom: "1rem",
                  border: "1px solid #ffcdd2",
                }}
              >
                {dobError}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: "100%",
                backgroundColor: primaryColor,
                color: "white",
                padding: "0.75rem",
                border: "none",
                borderRadius: "8px",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.7 : 1,
                transition: "opacity 0.3s ease",
              }}
            >
              {isLoading ? "Verifying..." : "Verify & Continue"}
            </button>
          </form>
        </div>

        {/* Contact Info */}
        {brandingInfo?.branding?.contactInfo && (
          <div
            style={{
              textAlign: "center",
              marginTop: "2rem",
              color: "#666",
              fontSize: "0.9rem",
            }}
          >
            <p>Need help? Contact us:</p>
            <p>
              📞 {brandingInfo.branding.contactInfo.phone} | ✉️{" "}
              {brandingInfo.branding.contactInfo.email}
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      {brandingInfo?.branding?.footerText && (
        <footer
          style={{
            backgroundColor: "#f5f5f5",
            padding: "1rem",
            textAlign: "center",
            color: "#666",
            fontSize: "0.8rem",
            marginTop: "3rem",
          }}
        >
          {brandingInfo.branding.footerText}
        </footer>
      )}
    </div>
  );
};

export default DOBVerification;
